import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { AuthContext } from "../../context/Auth/AuthContext";
import { loadCompanyPromptValues } from "../../services/companyPromptValues";
import api from "../../services/api";
import { agentEditorForm, agentEditorPayload } from "../../helpers/agentEditor";

export default function useAgentEditor({ promptId, templateSlug, allowed }) {
  const { user, socket } = useContext(AuthContext);
  const [resources, setResources] = useState({ models: [], credentials: [], queues: [], bases: [] });
  const [savedPrompt, setSavedPrompt] = useState(null);
  const [origin, setOrigin] = useState(null);
  const [form, setForm] = useState(() => agentEditorForm());
  const [baseline, setBaseline] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [retry, setRetry] = useState(0);
  const idempotencyKey = useRef(uuidv4());
  const savingRef = useRef(false);

  useEffect(() => {
    if (!allowed) return;
    let active = true;
    setLoading(true);
    setLoadError(false);
    Promise.all([
      api.get("/ai/models", { params: { purpose: "conversation" } }),
      api.get("/ai/credentials"), api.get("/queue"),
      api.get("/knowledge-bases", { params: { pageSize: 50, status: "active" } }),
      promptId ? api.get(`/prompt/${promptId}`) : Promise.resolve(null),
      !promptId && templateSlug ? api.get(`/agent-templates/${encodeURIComponent(templateSlug)}`) : Promise.resolve(null),
      api.get("/prompt/values-context"),
      loadCompanyPromptValues(),
    ]).then(([models, credentials, queues, bases, promptResponse, templateResponse, context, companyValues]) => {
      if (!active) return;
      const prompt = promptResponse?.data;
      const template = templateResponse?.data;
      const next = {
        companyName: context.data.companyName || "", companyValues,
        models: models.data.models || [], credentials: credentials.data || [], queues: queues.data || [],
        bases: Array.from(new Map([...(prompt?.knowledge?.bases || []), ...(bases.data.knowledgeBases || [])].map(base => [base.id, base])).values()),
      };
      const initial = agentEditorForm({ prompt, template, ...next });
      setResources(next);
      setSavedPrompt(prompt || null);
      setOrigin(prompt?.origin || (template ? { templateId: template.id, slug: template.slug, ...template.publishedVersion } : null));
      setForm(initial);
      setBaseline(JSON.stringify(initial));
    }).catch(() => active && setLoadError(true)).finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [allowed, promptId, templateSlug, retry, user?.companyId]);

  useEffect(() => {
    if (!allowed) return undefined;
    let active = true;
    const refresh = () => loadCompanyPromptValues().then(companyValues => {
      if (active) setResources(current => ({ ...current, companyValues }));
    }).catch(() => { if (active) setResources(current => ({ ...current, companyValues: [] })); });
    const event = `company-${user?.companyId}-prompt-values`;
    socket?.on(event, refresh);
    window.addEventListener("focus", refresh);
    return () => { active = false; socket?.off(event, refresh); window.removeEventListener("focus", refresh); };
  }, [allowed, user?.companyId, socket]);

  const setField = useCallback((field, value) => setForm(current => ({ ...current, [field]: value })), []);
  const rememberBases = useCallback(bases => setResources(current => ({ ...current,
    bases: Array.from(new Map([...current.bases, ...bases].map(base => [base.id, base])).values()),
  })), []);
  const refreshCredentials = async () => {
    const { data } = await api.get("/ai/credentials");
    setResources(current => ({ ...current, credentials: data || [] }));
  };
  const save = async () => {
    if (savingRef.current) return null;
    savingRef.current = true;
    setSaving(true);
    setSaveError("");
    try {
      const payload = agentEditorPayload(form, origin);
      const response = origin
        ? promptId
          ? await api.put(`/prompt/${promptId}/template-configuration`, payload)
          : await api.post(`/agent-templates/${origin.templateId}/instantiate`, payload, { headers: { "Idempotency-Key": idempotencyKey.current } })
        : promptId ? await api.put(`/prompt/${promptId}`, payload) : await api.post("/prompt", payload);
      const prompt = response.data.prompt && typeof response.data.prompt === "object" ? response.data.prompt : response.data;
      const updated = { ...prompt, bindings: savedPrompt?.bindings || prompt.bindings };
      const next = agentEditorForm({ prompt: updated, ...resources });
      setSavedPrompt(updated);
      setOrigin(prompt.origin || null);
      setForm(next);
      setBaseline(JSON.stringify(next));
      return updated;
    } catch (error) {
      const code = String(error.response?.data?.error || "");
      setSaveError(code.includes("SMART_VALUES") ? "Revise as variáveis e as instruções preenchidas. Suas alterações foram mantidas."
        : code.includes("CREDENTIAL") ? "Revise a credencial selecionada em Configurações. Suas alterações foram mantidas."
        : code.includes("KNOWLEDGE") ? "Revise a disponibilidade das bases e a política em Conhecimento. Suas alterações foram mantidas."
        : code.includes("CUSTOM_REVIEW") ? "Confirme a substituição das instruções personalizadas antes de salvar."
        : "Não foi possível salvar o agente. Suas alterações foram mantidas; revise os campos e tente novamente.");
      return null;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };
  return { resources, savedPrompt, origin, form, setForm, setField, loading, loadError, saving, saveError,
    dirty: !loading && Boolean(baseline) && JSON.stringify(form) !== baseline,
    retryLoad: () => setRetry(current => current + 1), refreshCredentials, rememberBases, save };
}
